import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import type { PickIdentityField } from 'modules/manager/domain/placeholder/placeholder-key-registry';
import { TemplateType } from 'modules/manager/domain/template-type';
import React from 'react';
import type { MessageDescriptor } from 'shared/i18n';
import { theme } from 'styles/theme/theme';
import UDInput from 'ud-form/components/input/component';
import { editorAccent } from 'modules/manager/ui/components/editor/editor-accent';

export type PickedIdentity = Partial<Record<PickIdentityField, number>>;

interface KeyIdentityFieldsProps {
  templateType: TemplateType;
  fields: readonly PickIdentityField[];
  value: PickedIdentity;
  onChange: (value: PickedIdentity) => void;
}

/** Templates where `teamNumber` is home (1) or away (2) rather than a row. */
const HOME_AWAY_TYPES: ReadonlySet<TemplateType> = new Set([
  TemplateType.SCHEDULE,
  TemplateType.RESULTS,
  TemplateType.MULTIPLE_SCHEDULE,
  TemplateType.MULTIPLE_RESULTS,
]);

const SIDES: readonly { value: number; label: MessageDescriptor }[] = [
  { value: 1, label: msg`1 · хозяева` },
  { value: 2, label: msg`2 · гости` },
];

function numberLabel(field: PickIdentityField, templateType: TemplateType): MessageDescriptor {
  if (field === 'matchNumber') return msg`Матч №`;
  if (field === 'teamNumber') return templateType === TemplateType.TOURNAMENT_TABLE ? msg`Строка` : msg`Команда №`;
  return templateType === TemplateType.TOP_PLAYERS_BY_INDICATORS ? msg`Место в рейтинге` : msg`Игрок №`;
}

const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

/**
 * Slot numbers asked for when a placeholder is created. The control follows the
 * meaning of the number on this template type: a home/away switch on a match,
 * a number field for a row, a rank or a match.
 */
const KeyIdentityFields: React.FC<KeyIdentityFieldsProps> = ({ templateType, fields, value, onChange }) => {
  const { t } = useLingui();
  if (fields.length === 0) return null;

  return (
    <Fields>
      {fields.map((field) =>
        field === 'teamNumber' && HOME_AWAY_TYPES.has(templateType) ? (
          <Field key={field}>
            <Name>{t`Команда`}</Name>
            <Segments role="group" aria-label={t`Команда`}>
              {SIDES.map((side) => (
                <Segment
                  key={side.value}
                  type="button"
                  aria-pressed={value.teamNumber === side.value}
                  onClick={() => onChange({ ...value, teamNumber: side.value })}
                >
                  {t(side.label)}
                </Segment>
              ))}
            </Segments>
          </Field>
        ) : (
          <Field key={field}>
            <NameLabel htmlFor={`key-identity-${field}`}>{t(numberLabel(field, templateType))}</NameLabel>
            <NumberBox>
              <UDInput
                id={`key-identity-${field}`}
                inputMode="numeric"
                value={value[field] ? String(value[field]) : ''}
                parser={digitsOnly}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  // Empty or 0 is not a slot — the field keeps the last valid number.
                  if (next >= 1) onChange({ ...value, [field]: next });
                }}
              />
            </NumberBox>
          </Field>
        ),
      )}
    </Fields>
  );
};

export default React.memo(KeyIdentityFields);

const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const nameStyles = css`
  font-size: 13px;
  color: ${theme.colors.InkSecondary};
`;

const Name = styled.span(nameStyles);

const NameLabel = styled.label(nameStyles);

const NumberBox = styled.div`
  width: 80px;
`;

const Segments = styled.div`
  display: inline-flex;
  overflow: hidden;
  border: 1px solid ${theme.colors.LineSubtle};
  border-radius: ${theme.radii.card};
`;

const Segment = styled.button`
  padding: 6px 12px;
  border: 0;
  background: ${theme.colors.SurfaceCard};
  color: ${theme.colors.InkSecondary};
  font-size: 13px;
  cursor: pointer;

  & + & {
    border-left: 1px solid ${theme.colors.LineSubtle};
  }

  &[aria-pressed='true'] {
    background: ${editorAccent.soft};
    color: ${editorAccent.ink};
    font-weight: 600;
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${editorAccent.ring};
  }
`;
